import React, { useEffect, useState, useMemo, useCallback } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Icon,
  PaginationComponent,
  Row,
  Col,
} from "../../components/Component";
import {
  Card,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  DropdownItem,
  DropdownMenu,
  UncontrolledDropdown,
  DropdownToggle,
} from "reactstrap";
import { handleGetApis, handlePostApis, handleDeleteApis } from "../../service/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ── Constants & Helpers ───────────────────────────────────────────────────────

const STATUS_COLOR = {
  unread: "danger",
  read: "info",
  replied: "success",
};

const ITEMS_PER_PAGE = 20;

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const EnquiryList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState(""); // "" (All) | "unread" | "read" | "replied"
  const [currentPage, setCurrentPage] = useState(1);

  // Action status loading tracker
  const [actionId, setActionId] = useState(null);

  // Detail Modal State
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailEnquiry, setDetailEnquiry] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete Confirm Modal State
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* ── Fetch Enquiries List ─────────────────────────────────── */
  const fetchEnquiries = useCallback(
    async (pg = currentPage) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: pg, limit: ITEMS_PER_PAGE });
        if (statusFilter) params.append("status", statusFilter);

        const res = await handleGetApis(`admin/enquiries?${params}`);
        if (res?.data?.success) {
          setEnquiries(res.data.data || []);
          setPagination(res.data.pagination || { total: 0, page: pg, total_pages: 1 });
        } else {
          toast.error(res?.data?.message || "Failed to load enquiries");
        }
      } catch (err) {
        console.error("Fetch enquiries error:", err);
        toast.error("Failed to load enquiries");
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchEnquiries(1);
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => {
    fetchEnquiries(currentPage);
  }, [currentPage]); // eslint-disable-line

  /* ── Summary Counts ───────────────────────────────────────── */
  const counts = useMemo(() => {
    const unread = enquiries.filter((e) => e.status === "unread").length;
    const read = enquiries.filter((e) => e.status === "read").length;
    const replied = enquiries.filter((e) => e.status === "replied").length;
    return { unread, read, replied };
  }, [enquiries]);

  /* ── View Enquiry Details (Auto-marked read by backend) ────── */
  const handleOpenDetail = async (item) => {
    setDetailEnquiry(item);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await handleGetApis(`admin/enquiry/${item.id}`);
      if (res?.data?.success && res.data.data) {
        setDetailEnquiry(res.data.data);
        // If it was unread, update local list status to read
        if (item.status === "unread") {
          setEnquiries((prev) =>
            prev.map((e) => (e.id === item.id ? { ...e, status: "read" } : e))
          );
        }
      }
    } catch (err) {
      console.error("Get enquiry by ID error:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  /* ── Update Status ────────────────────────────────────────── */
  const updateStatus = async (id, status) => {
    setActionId(id);
    try {
      const res = await handlePostApis("admin/enquiry/status", { id, status });
      if (res?.data?.success) {
        setEnquiries((prev) =>
          prev.map((e) => (e.id === id ? { ...e, status } : e))
        );
        if (detailEnquiry?.id === id) {
          setDetailEnquiry((prev) => ({ ...prev, status }));
        }
        toast.success(`Enquiry marked as ${status}`);
      } else {
        toast.error(res?.data?.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setActionId(null);
    }
  };

  /* ── Delete Enquiry ───────────────────────────────────────── */
  const openDeleteModal = (item) => {
    setDeleteTarget(item);
    setDeleteOpen(true);
  };

  const confirmDeleteEnquiry = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await handleDeleteApis("admin/enquiry", { id: deleteTarget.id });
      if (res?.data?.success || res?.status === 200) {
        setEnquiries((prev) => prev.filter((e) => e.id !== deleteTarget.id));
        setPagination((p) => ({ ...p, total: Math.max(0, p.total - 1) }));
        toast.success("Enquiry deleted successfully");
        setDeleteOpen(false);
        if (detailEnquiry?.id === deleteTarget.id) setDetailOpen(false);
      } else {
        toast.error(res?.data?.message || "Failed to delete enquiry");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete enquiry");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Head title="User Enquiries" />
      <ToastContainer position="top-right" autoClose={3000} />

      <Content>
        {/* ── Page Header ─────────────────────────────────────── */}
        <BlockHead size="sm" className="mb-3">
          <BlockBetween className="align-items-center flex-wrap gap-2">
            <BlockHeadContent>
              <BlockTitle page className="mb-1">
                User Enquiries
              </BlockTitle>
              <p className="text-soft fs-13px mb-0">
                View, reply, and manage customer contact messages &amp; inquiries.
              </p>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="light"
                size="sm"
                outline
                className="bg-white"
                onClick={() => fetchEnquiries(currentPage)}
                disabled={loading}
              >
                <Icon name="reload" /> <span className="ms-1">Refresh</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          {/* ── Summary Counters ─────────────────────────────────── */}
          <Row className="g-3 mb-4">
            {[
              { label: "Total Enquiries", value: pagination.total, icon: "chat-fill", color: "primary" },
              { label: "Unread", value: counts.unread, icon: "bell-fill", color: "danger" },
              { label: "Read / Viewed", value: counts.read, icon: "mail-fill", color: "info" },
              { label: "Replied", value: counts.replied, icon: "check-circle-fill", color: "success" },
            ].map((c) => (
              <Col sm="6" lg="3" key={c.label}>
                <Card className="card-bordered h-100">
                  <div className="card-inner py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="text-soft fs-11px fw-bold text-uppercase d-block mb-1">
                          {c.label}
                        </span>
                        <h4 className={`mb-0 text-${c.color} fw-bold`}>{c.value}</h4>
                      </div>
                      <div
                        className={`rounded-circle bg-${c.color}-dim d-flex align-items-center justify-content-center`}
                        style={{ width: 44, height: 44 }}
                      >
                        <Icon name={c.icon} className={`text-${c.color} fs-20px`} />
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── Enquiries Data Table with Integrated Filter Bar ──── */}
          <Card className="card-bordered">
            <div className="card-inner-group">
              {/* Integrated Filter Header */}
              <div className="card-inner py-3 border-bottom">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-soft fs-12px fw-bold text-uppercase me-1">
                      Filter by Status:
                    </span>
                    {[
                      { key: "", label: "All Enquiries", color: "primary", count: pagination.total },
                      { key: "unread", label: "Unread", color: "danger", count: counts.unread },
                      { key: "read", label: "Read", color: "info", count: counts.read },
                      { key: "replied", label: "Replied", color: "success", count: counts.replied },
                    ].map((t) => {
                      const isActive = statusFilter === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          className={`btn btn-sm rounded-pill d-inline-flex align-items-center gap-1 ${
                            isActive
                              ? `btn-${t.color} text-white shadow-sm`
                              : "btn-outline-light text-dark bg-white"
                          }`}
                          style={{ padding: "5px 14px", fontSize: "13px" }}
                          onClick={() => setStatusFilter(t.key)}
                        >
                          <span>{t.label}</span>
                          <span
                            className={`badge rounded-pill ${
                              isActive
                                ? "bg-white text-dark"
                                : `bg-${t.color}-dim text-${t.color}`
                            }`}
                            style={{ fontSize: "11px", padding: "2px 6px" }}
                          >
                            {t.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="card-inner p-0">
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner color="primary" />
                    <span className="ms-2 text-soft fs-14px">Loading enquiries...</span>
                  </div>
                ) : enquiries.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="text-soft mb-2">
                      <Icon name="inbox" style={{ fontSize: 54, opacity: 0.4 }} />
                    </div>
                    <h6 className="title text-dark mb-1">No enquiries found</h6>
                    <p className="text-soft fs-13px">
                      {statusFilter ? `There are no "${statusFilter}" enquiries.` : "No customer enquiries have been submitted yet."}
                    </p>
                  </div>
                ) : (
                  <DataTableBody>
                    <DataTableHead>
                      <DataTableRow className="nk-tb-col-check">
                        <span>#</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>Customer</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span>Subject &amp; Message</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>Status</span>
                      </DataTableRow>
                      <DataTableRow size="sm">
                        <span>Date Received</span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools text-end">
                        <span>Actions</span>
                      </DataTableRow>
                    </DataTableHead>

                    {enquiries.map((item, idx) => (
                      <DataTableItem key={item.id}>
                        {/* Serial Number */}
                        <DataTableRow className="nk-tb-col-check">
                          <span className="text-soft fw-bold fs-12px">
                            {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                          </span>
                        </DataTableRow>

                        {/* Customer Info */}
                        <DataTableRow>
                          <div className="user-card">
                            <div
                              className={`user-avatar sm bg-${
                                item.status === "unread" ? "danger-dim text-danger" : "primary-dim text-primary"
                              }`}
                            >
                              <span>{item.name ? item.name.charAt(0).toUpperCase() : "U"}</span>
                            </div>
                            <div className="user-info">
                              <span className={`tb-lead ${item.status === "unread" ? "fw-bold text-dark" : ""}`}>
                                {item.name || "Anonymous"}
                              </span>
                              <span className="sub-text text-muted">{item.email || "No email"}</span>
                            </div>
                          </div>
                        </DataTableRow>

                        {/* Subject & Message Preview */}
                        <DataTableRow size="md">
                          <div
                            className="cursor-pointer"
                            onClick={() => handleOpenDetail(item)}
                            title="Click to view full message"
                          >
                            <span className={`tb-lead d-block ${item.status === "unread" ? "fw-bold text-dark" : ""}`}>
                              {item.subject || "No Subject"}
                            </span>
                            <span
                              className="text-soft fs-12px d-block text-truncate"
                              style={{ maxWidth: 300 }}
                            >
                              {item.message_preview || "—"}
                            </span>
                          </div>
                        </DataTableRow>

                        {/* Status Badge */}
                        <DataTableRow>
                          <Badge
                            color={STATUS_COLOR[item.status] || "secondary"}
                            className="badge-dim rounded-pill text-capitalize px-2 py-1"
                          >
                            {item.status}
                          </Badge>
                        </DataTableRow>

                        {/* Received Date */}
                        <DataTableRow size="sm">
                          <span className="tb-sub">{formatDate(item.created_at)}</span>
                        </DataTableRow>

                        {/* Actions */}
                        <DataTableRow className="nk-tb-col-tools text-end">
                          <ul className="nk-tb-actions gx-1 my-n1">
                            {/* View Detail Button */}
                            <li>
                              <Button
                                size="xs"
                                color="light"
                                outline
                                className="btn-icon"
                                title="View Enquiry"
                                onClick={() => handleOpenDetail(item)}
                              >
                                <Icon name="eye" />
                              </Button>
                            </li>

                            {/* Mailto Reply */}
                            {item.email && (
                              <li>
                                <a
                                  href={`mailto:${item.email}?subject=Re: ${encodeURIComponent(item.subject || "Your Enquiry")}`}
                                  className="btn btn-xs btn-outline-light btn-icon text-primary"
                                  title="Reply via Email"
                                  onClick={() => {
                                    if (item.status !== "replied") {
                                      updateStatus(item.id, "replied");
                                    }
                                  }}
                                >
                                  <Icon name="mail" />
                                </a>
                              </li>
                            )}

                            {/* Dropdown Options */}
                            <li>
                              <UncontrolledDropdown>
                                <DropdownToggle tag="a" className="btn btn-xs btn-trigger btn-icon">
                                  <Icon name="more-h" />
                                </DropdownToggle>
                                <DropdownMenu end>
                                  <ul className="link-list-opt no-bdr">
                                    <li>
                                      <DropdownItem
                                        tag="a"
                                        href="#view"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleOpenDetail(item);
                                        }}
                                      >
                                        <Icon name="eye" />
                                        <span>View Message</span>
                                      </DropdownItem>
                                    </li>
                                    {item.status !== "unread" && (
                                      <li>
                                        <DropdownItem
                                          tag="a"
                                          href="#unread"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            updateStatus(item.id, "unread");
                                          }}
                                        >
                                          <Icon name="bell" />
                                          <span>Mark as Unread</span>
                                        </DropdownItem>
                                      </li>
                                    )}
                                    {item.status !== "read" && (
                                      <li>
                                        <DropdownItem
                                          tag="a"
                                          href="#read"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            updateStatus(item.id, "read");
                                          }}
                                        >
                                          <Icon name="check" />
                                          <span>Mark as Read</span>
                                        </DropdownItem>
                                      </li>
                                    )}
                                    {item.status !== "replied" && (
                                      <li>
                                        <DropdownItem
                                          tag="a"
                                          href="#replied"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            updateStatus(item.id, "replied");
                                          }}
                                        >
                                          <Icon name="check-circle" />
                                          <span>Mark as Replied</span>
                                        </DropdownItem>
                                      </li>
                                    )}
                                    <li className="divider"></li>
                                    <li>
                                      <DropdownItem
                                        tag="a"
                                        href="#delete"
                                        className="text-danger"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openDeleteModal(item);
                                        }}
                                      >
                                        <Icon name="trash" />
                                        <span>Delete Enquiry</span>
                                      </DropdownItem>
                                    </li>
                                  </ul>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </li>
                          </ul>
                        </DataTableRow>
                      </DataTableItem>
                    ))}
                  </DataTableBody>
                )}
              </div>

              {/* Pagination */}
              {!loading && pagination.total_pages > 1 && (
                <div className="card-inner py-3">
                  <PaginationComponent
                    itemPerPage={ITEMS_PER_PAGE}
                    totalItems={pagination.total}
                    paginate={(p) => setCurrentPage(p)}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </div>
          </Card>
        </Block>
      </Content>

      {/* ══════════════════════════════════════════════════════════
          ENQUIRY DETAIL MODAL
      ══════════════════════════════════════════════════════════ */}
      {detailEnquiry && (
        <Modal
          isOpen={detailOpen}
          toggle={() => setDetailOpen(false)}
          size="lg"
          className="modal-dialog-top"
        >
          <ModalHeader toggle={() => setDetailOpen(false)}>
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold fs-16px">Customer Enquiry</span>
              <Badge
                color={STATUS_COLOR[detailEnquiry.status] || "secondary"}
                className="badge-dim rounded-pill text-capitalize px-2 py-1 fs-11px"
              >
                {detailEnquiry.status}
              </Badge>
            </div>
          </ModalHeader>
          <ModalBody className="modal-body-md pb-4">
            {detailLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
                <span className="d-block text-soft mt-2 fs-13px">Loading details...</span>
              </div>
            ) : (
              <div>
                {/* Customer Details Box */}
                <div className="p-3 bg-light rounded mb-3 border">
                  <Row className="g-3 align-items-center">
                    <Col sm="6" xs="12">
                      <span className="text-soft fs-11px text-uppercase fw-bold d-block mb-1">
                        Customer Name
                      </span>
                      <strong className="fs-15px text-dark">
                        {detailEnquiry.name || "Anonymous"}
                      </strong>
                    </Col>
                    <Col sm="6" xs="12">
                      <span className="text-soft fs-11px text-uppercase fw-bold d-block mb-1">
                        Email Address
                      </span>
                      {detailEnquiry.email ? (
                        <a
                          href={`mailto:${detailEnquiry.email}?subject=Re: ${encodeURIComponent(detailEnquiry.subject || "")}`}
                          className="fs-14px text-primary fw-bold d-inline-flex align-items-center"
                        >
                          <Icon name="mail" className="me-1" />
                          {detailEnquiry.email}
                        </a>
                      ) : (
                        <span className="text-muted fs-14px">—</span>
                      )}
                    </Col>
                  </Row>
                </div>

                {/* Subject */}
                <div className="mb-3">
                  <span className="text-soft fs-11px text-uppercase fw-bold d-block mb-1">
                    Subject
                  </span>
                  <div className="fs-14px fw-bold text-dark p-2 px-3 bg-white border rounded">
                    {detailEnquiry.subject || "No Subject"}
                  </div>
                </div>

                {/* Message Body */}
                <div className="mb-3">
                  <span className="text-soft fs-11px text-uppercase fw-bold d-block mb-1">
                    Full Message
                  </span>
                  <div
                    className="p-3 border rounded text-dark fs-14px"
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: "1.7",
                      minHeight: 120,
                      maxHeight: 220,
                      overflowY: "auto",
                      backgroundColor: "#f8f9fc",
                    }}
                  >
                    {detailEnquiry.message || detailEnquiry.message_preview || "No message content."}
                  </div>
                </div>

                <div className="text-soft fs-12px mb-3">
                  Received on: <strong>{formatDate(detailEnquiry.created_at)}</strong>
                </div>

                {/* Action Buttons Inside Modal */}
                <div className="pt-3 border-top mt-4 mb-2">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      {detailEnquiry.email && (
                        <a
                          href={`mailto:${detailEnquiry.email}?subject=Re: ${encodeURIComponent(detailEnquiry.subject || "")}`}
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            if (detailEnquiry.status !== "replied") {
                              updateStatus(detailEnquiry.id, "replied");
                            }
                          }}
                        >
                          <Icon name="send" /> <span className="ms-1">Reply via Email</span>
                        </a>
                      )}
                      {detailEnquiry.status !== "replied" && (
                        <Button
                          color="success"
                          size="sm"
                          disabled={actionId === detailEnquiry.id}
                          onClick={() => updateStatus(detailEnquiry.id, "replied")}
                        >
                          <Icon name="check-circle" /> <span className="ms-1">Mark as Replied</span>
                        </Button>
                      )}
                      {detailEnquiry.status !== "unread" && (
                        <Button
                          color="warning"
                          size="sm"
                          className="btn-dim"
                          disabled={actionId === detailEnquiry.id}
                          onClick={() => updateStatus(detailEnquiry.id, "unread")}
                        >
                          <Icon name="bell" /> <span className="ms-1">Mark as Unread</span>
                        </Button>
                      )}
                    </div>

                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={() => {
                        setDetailOpen(false);
                        openDeleteModal(detailEnquiry);
                      }}
                    >
                      <Icon name="trash" /> <span className="ms-1">Delete</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal isOpen={deleteOpen} toggle={() => !deleteLoading && setDeleteOpen(false)} size="sm" centered>
        <ModalHeader toggle={() => !deleteLoading && setDeleteOpen(false)}>
          Confirm Deletion
        </ModalHeader>
        <ModalBody>
          <p className="mb-0 fs-14px">
            Are you sure you want to delete this enquiry from{" "}
            <strong>{deleteTarget?.name || "this customer"}</strong>? This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" size="sm" onClick={confirmDeleteEnquiry} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <Spinner size="sm" className="me-1" /> Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}
          </Button>
          <Button color="light" size="sm" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default EnquiryList;
